import React, { useEffect, useState } from 'react';
import type {
  CentralAnalysis,
  CentralFinding,
  CentralKpi,
  CentralOverview,
  CentralRecommendation,
} from '../../services/MendsVisionService';
import { describeDelta, formatDateTime, formatKpiValue, formatMinutes } from '../../lib/centralFormat';
import type { CentralTargetResult, PlanState } from '../../lib/centralActions';
import { PLAN_STATUS_LABEL, planPreviewCounts, summarizeBatch } from '../../lib/centralActions';
import { daysLabel, formatBrl, formatDelta, formatMoney } from '../../../utils/revenueFormat';
import TemperatureChip from '../revenue/TemperatureChip';
import { MaiDrawer } from '../MaiDrawer';
import { CentralTrendChart } from './CentralTrendChart';
import { CentralAgentCards } from './CentralAgents';

/** Mesmos números da tela Hoje (soma das conexões filtradas), lidos do mesmo endpoint (CI-04). */
export type CentralNowCounts = {
  aiAttending: number;
  awaitingReply: number;
  needsHuman: number;
  riskSilent: number;
  approvalsPending: number;
  /** IB-05 — conversas fora do prazo de atendimento, escaladas à equipe. */
  slaBreached: number;
};

export type BatchState = {
  running: boolean;
  done: number;
  total: number;
  results: CentralTargetResult[];
};

type Props = {
  overview: CentralOverview;
  now: CentralNowCounts | null;
  nowError: string | null;
  analysis: CentralAnalysis | null;
  analysisLoading: boolean;
  onRetryAnalysis: () => void;
  batches: Record<string, BatchState>;
  onRunRecommendation: (rec: CentralRecommendation) => void;
  /** Estado do "Executar plano" por recomendação. */
  plans?: Record<string, PlanState>;
  /** Executa o plano já confirmado na prévia. */
  onExecutePlan?: (rec: CentralRecommendation) => void;
  onOpen: (path: string) => void;
  onShowAgents: () => void;
  /** Top assuntos (carregados à parte: a classificação pode levar mais tempo que os números). */
  topicsSlot?: React.ReactNode;
};

const TONE_LABEL: Record<CentralFinding['tone'], string> = {
  risk: 'Risco',
  opportunity: 'Oportunidade',
  attention: 'Atenção',
};

const KpiCard: React.FC<{ kpi: CentralKpi; comparison: string; onOpen: (p: string) => void }> = ({ kpi, comparison, onOpen }) => {
  const [showHow, setShowHow] = useState(false);
  const delta = describeDelta(kpi);
  const value = formatKpiValue(kpi.value, kpi.unit, kpi.currency);
  return (
    <article className={`mai-ci-kpi ${kpi.available ? '' : 'is-unavailable'}`}>
      <div className="mai-ci-kpi-top">
        <span className="mai-ci-kpi-label">{kpi.label}</span>
        <button
          type="button"
          className="mai-ci-how"
          aria-expanded={showHow}
          aria-label={`Como é calculado: ${kpi.label}`}
          onClick={() => setShowHow((s) => !s)}
        >
          ?
        </button>
      </div>
      {kpi.available ? (
        <>
          <p className="mai-ci-kpi-value">{value}</p>
          {kpi.snapshot ? (
            <p className="mai-ci-kpi-delta is-neutral">agora (não depende do período)</p>
          ) : (
            <p className={`mai-ci-kpi-delta is-${delta.tone}`}>
              {delta.text}
              {delta.text && <span> {kpi.comparisonLabel || comparison}</span>}
            </p>
          )}
          {kpi.hint && <p className="mai-ci-kpi-hint">{kpi.hint}</p>}
        </>
      ) : (
        <>
          <p className="mai-ci-kpi-value is-muted">Indisponível</p>
          <p className="mai-ci-kpi-hint">{kpi.unavailableReason}</p>
        </>
      )}
      {showHow && <p className="mai-ci-kpi-how">{kpi.howCalculated}</p>}
      {kpi.path && kpi.available && (
        <button type="button" className="mai-ci-link" onClick={() => onOpen(kpi.path!)}>Abrir</button>
      )}
    </article>
  );
};

const FindingDrawer: React.FC<{ finding: CentralFinding | null; onClose: () => void; onOpen: (p: string) => void }> = ({
  finding,
  onClose,
  onOpen,
}) => (
  <MaiDrawer
    open={!!finding}
    wide
    title={finding?.title || ''}
    subtitle={finding ? `${finding.count} caso${finding.count === 1 ? '' : 's'} · ${finding.scope === 'now' ? 'situação de agora' : 'no período filtrado'}` : null}
    onClose={onClose}
    footer={finding?.listPath ? (
      <button type="button" className="mai-btn" onClick={() => onOpen(finding.listPath!)}>
        Abrir a lista completa
      </button>
    ) : null}
  >
    {finding && (
      <>
        <div className="mai-drawer-block">
          <div className="mai-drawer-block-label">O que foi visto</div>
          <p>{finding.what}</p>
        </div>
        <div className="mai-drawer-block">
          <div className="mai-drawer-block-label">Por que importa</div>
          <p>{finding.why}</p>
        </div>
        <div className="mai-drawer-block">
          <div className="mai-drawer-block-label">Casos ({finding.cases.length < finding.count ? `${finding.cases.length} de ${finding.count}` : finding.count})</div>
          <ul className="mai-ci-cases">
            {finding.cases.map((c) => (
              <li key={`${c.sectorId}-${c.contactId}-${c.at}`}>
                <div className="mai-ci-case-main">
                  <strong>{c.contactName}</strong>
                  <span className="mai-ci-case-meta">
                    {[formatDateTime(c.at), c.owner, c.value, c.sectorName].filter(Boolean).join(' · ')}
                  </span>
                  <span className="mai-ci-case-evidence">{c.evidence}</span>
                </div>
                <div className="mai-ci-case-actions">
                  <button type="button" className="mai-btn" onClick={() => onOpen(c.path)}>Abrir conversa</button>
                  {c.secondaryPath && (
                    <button type="button" className="mai-btn mai-btn-ghost" onClick={() => onOpen(c.secondaryPath!)}>
                      {c.secondaryLabel || 'Abrir'}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="mai-drawer-block">
          <div className="mai-drawer-block-label">O que os dados não dizem</div>
          <ul className="mai-drawer-list">
            {finding.limits.map((l) => <li key={l}>{l}</li>)}
          </ul>
        </div>
        <div className="mai-drawer-block">
          <div className="mai-drawer-block-label">Como é calculado</div>
          <p className="mai-ci-muted">{finding.howCalculated}</p>
        </div>
      </>
    )}
  </MaiDrawer>
);

/** Prévia do plano: cada passo, se fala com o cliente (vira aprovação) ou é interno, e os alvos. */
const PlanPreview: React.FC<{
  rec: CentralRecommendation;
  state?: PlanState;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ rec, state, onConfirm, onCancel }) => {
  const plan = rec.plan!;
  const counts = planPreviewCounts(plan);
  return (
    <div className="mai-ci-plan" data-testid={`plan-preview-${rec.id}`}>
      <p className="mai-ci-plan-intro">
        <strong>Prévia do plano</strong> — {counts.clients} {counts.clients === 1 ? 'cliente' : 'clientes'}:{' '}
        {counts.internal > 0 && `${counts.internal} ${counts.internal === 1 ? 'passo interno feito' : 'passos internos feitos'} na hora`}
        {counts.internal > 0 && counts.approvals > 0 && ' e '}
        {counts.approvals > 0 && `${counts.approvals} ${counts.approvals === 1 ? 'mensagem que vai' : 'mensagens que vão'} para a fila de aprovação (nada é enviado sem aprovar)`}.
      </p>
      <ol className="mai-ci-plan-steps">
        {plan.steps.map((step) => (
          <li key={step.id} className={step.talksToClient ? 'is-client' : 'is-internal'}>
            <div className="mai-ci-plan-step-head">
              <strong>{step.label}</strong>
              <span className="mai-ci-plan-badge">{step.talksToClient ? 'Fala com o cliente · vira aprovação' : 'Interno · feito na hora'}</span>
            </div>
            <p>{step.what}</p>
            <ul>
              {step.targets.map((t) => (
                <li key={`${step.id}-${t.sectorId}-${t.contactId}`}>
                  <span className="mai-ci-plan-target">{t.contactName}</span>
                  {t.detail && <span className="mai-ci-muted"> — {t.detail}</span>}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
      {state?.error && <p className="mai-ci-plan-error" role="alert">{state.error}</p>}
      <div className="mai-ci-plan-actions">
        <button type="button" className="mai-btn mai-btn-ghost" onClick={onCancel} disabled={state?.running}>Cancelar</button>
        <button type="button" className="mai-btn" onClick={onConfirm} disabled={state?.running}>
          {state?.running ? 'Executando…' : 'Confirmar e executar'}
        </button>
      </div>
    </div>
  );
};

/** Rótulo em português do resultado de um alvo; código novo do servidor nunca aparece cru. */
export function planStatusLabel(status?: string | null): string {
  return (PLAN_STATUS_LABEL as Record<string, string>)[(status || '').toLowerCase()] ?? 'Situação não reconhecida';
}

/** Resultado da execução: o que foi feito em cada passo, por cliente. */
const PlanResult: React.FC<{ run: NonNullable<PlanState['run']> }> = ({ run }) => (
  <div className="mai-ci-rec-result mai-ci-plan-result" role="status" data-testid={`plan-result-${run.recommendationId}`}>
    <p>
      <strong>{run.alreadyRan ? 'Já executado hoje: ' : 'Plano executado: '}{run.summary}</strong>
    </p>
    {run.steps.map((step) => (
      <div key={step.stepId}>
        <p className="mai-ci-plan-result-step">{step.label}</p>
        <ul>
          {step.results.map((r) => (
            <li key={`${step.stepId}-${r.sectorId}-${r.contactId}`} className={`is-${r.status}`} title={planStatusLabel(r.status)}>
              {r.contactName}: {r.message}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

/** Receita (Radar, lido dos cards): fechado no mês com os casos clicáveis, paradas e sem valor. */
const CentralRevenuePanel: React.FC<{
  revenue: NonNullable<CentralOverview['revenue']>;
  multiSector: boolean;
  onOpen: (p: string) => void;
}> = ({ revenue, multiSector, onOpen }) => {
  const [showCases, setShowCases] = useState(false);
  const m = revenue.closedMonth;
  const delta = formatDelta(m.deltaPct);
  return (
    <section className="mai-ci-panel mai-ci-revenue" aria-label="Receita" data-testid="central-revenue">
      <div className="mai-ci-section-head">
        <h2>Receita</h2>
        <span className="mai-ci-muted">Valores registrados nos cards{multiSector ? ', somando as conexões filtradas' : ''}. Nada é estimado.</span>
      </div>
      <div className="mai-ci-revenue-grid">
        <button
          type="button"
          className="mai-ci-revenue-tile"
          onClick={() => setShowCases((v) => !v)}
          disabled={!revenue.hasWonColumn}
          aria-expanded={showCases}
        >
          <span>Fechado em {m.label}</span>
          <strong data-testid="central-closed-month">{revenue.hasWonColumn ? formatBrl(m.value) : 'Indisponível'}</strong>
          <small>
            {revenue.hasWonColumn
              ? `${m.count} ${m.count === 1 ? 'venda' : 'vendas'} · ${m.previousLabel} até o mesmo dia: ${formatBrl(m.previousValue)}${delta ? ` (${delta})` : ''}`
              : 'O funil não tem coluna de Ganho'}
          </small>
        </button>
        <button type="button" className="mai-ci-revenue-tile" onClick={() => onOpen('/central-inteligencia?tab=oportunidades')}>
          <span>Em negociação</span>
          <strong>{formatBrl(revenue.pipelineValue)}</strong>
          <small>Ponderado pela temperatura: {formatBrl(revenue.weighted)}</small>
        </button>
        <button type="button" className="mai-ci-revenue-tile" onClick={() => onOpen('/central-inteligencia?tab=oportunidades&radar=paradas')}>
          <span>Paradas há mais de {daysLabel(revenue.stalledDays)}</span>
          <strong>{revenue.stalledCount}</strong>
          <small>{revenue.stalledValue > 0 ? `${formatBrl(revenue.stalledValue)} parados` : 'Ver no Radar'}</small>
        </button>
        <button type="button" className="mai-ci-revenue-tile" onClick={() => onOpen('/central-inteligencia?tab=oportunidades&radar=sem-valor')}>
          <span>Sem valor</span>
          <strong>{revenue.withoutValueCount}</strong>
          <small>{revenue.withoutValueCount > 0 ? 'Registrar valores' : 'Todas têm valor'}</small>
        </button>
      </div>
      {showCases && (
        <ul className="mai-ci-revenue-cases" data-testid="central-closed-cases">
          {m.cases.length === 0 && <li className="mai-ci-muted">Nenhuma venda entrou em Ganho neste mês ainda.</li>}
          {m.cases.map((c) => (
            <li key={c.cardId}>
              <button type="button" onClick={() => onOpen(c.path)}>{c.contactName}</button>
              <span className="mai-ci-muted">
                {c.title ? `${c.title} · ` : ''}{c.wonAt ? new Date(c.wonAt).toLocaleDateString('pt-BR') : ''}
              </span>
              <TemperatureChip temperature={c.temperature} compact />
              <strong>{c.value != null ? formatMoney(c.value, c.currency) : 'Sem valor'}</strong>
            </li>
          ))}
          {m.count > m.cases.length && <li className="mai-ci-muted">Mostrando {m.cases.length} de {m.count}.</li>}
          {m.wonWithoutDate > 0 && (
            <li className="mai-ci-muted">
              {m.wonWithoutDate} {m.wonWithoutDate === 1 ? 'card em Ganho não tem' : 'cards em Ganho não têm'} data de entrada (anteriores à trilha) e {m.wonWithoutDate === 1 ? 'fica' : 'ficam'} fora do mês.
            </li>
          )}
        </ul>
      )}
    </section>
  );
};

const RecommendationCard: React.FC<{
  rec: CentralRecommendation;
  batch?: BatchState;
  plan?: PlanState;
  onRun: (r: CentralRecommendation) => void;
  onExecutePlan?: (r: CentralRecommendation) => void;
  onOpen: (p: string) => void;
}> = ({ rec, batch, plan, onRun, onExecutePlan, onOpen }) => {
  const [previewing, setPreviewing] = useState(false);
  const status = rec.status;
  // Terminou de executar: fecha a prévia (o resultado aparece no lugar).
  useEffect(() => {
    if (plan?.run) setPreviewing(false);
  }, [plan?.run]);
  const hasPlan = !!rec.plan && rec.plan.steps.length > 0 && !!onExecutePlan;
  const isBatch = !hasPlan && rec.actionKind === 'follow_up_batch';
  const run = plan?.run ?? rec.plan?.lastRun ?? null;
  return (
    <article className="mai-ci-rec">
      <div className="mai-ci-rec-body">
        <p className="mai-ci-rec-what"><span>O que identifiquei</span>{rec.what}</p>
        <p className="mai-ci-rec-why"><span>Por que importa</span>{rec.why}</p>
        <p className="mai-ci-rec-action"><span>Ação</span>{rec.action}</p>
        {(isBatch || hasPlan) && status.total > 0 && (
          <p className="mai-ci-rec-status">
            Já pedido pela Central: {[
              status.pending ? `${status.pending} aguardando aprovação` : null,
              status.executed ? `${status.executed} aprovado${status.executed === 1 ? '' : 's'} e executado${status.executed === 1 ? '' : 's'}` : null,
              status.rejected ? `${status.rejected} recusado${status.rejected === 1 ? '' : 's'}` : null,
              status.other ? `${status.other} expirado${status.other === 1 ? '' : 's'}/substituído${status.other === 1 ? '' : 's'}` : null,
            ].filter(Boolean).join(' · ')}
          </p>
        )}
        {hasPlan && previewing && (
          <PlanPreview
            rec={rec}
            state={plan}
            onCancel={() => setPreviewing(false)}
            onConfirm={() => onExecutePlan!(rec)}
          />
        )}
        {hasPlan && run && <PlanResult run={run} />}
        {batch && (
          <div className="mai-ci-rec-result" role="status">
            {batch.running ? (
              <p>Criando aprovações… {batch.done} de {batch.total}</p>
            ) : (
              <>
                <p><strong>{summarizeBatch(batch.results)}</strong></p>
                <ul>
                  {batch.results.map((r) => (
                    <li key={`${r.sectorId}-${r.contactId}`} className={`is-${r.kind}`}>
                      {r.contactName}: {r.message}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
      <div className="mai-ci-rec-actions">
        {hasPlan ? (
          <>
            <button
              type="button"
              className="mai-btn"
              disabled={plan?.running || previewing}
              onClick={() => setPreviewing(true)}
            >
              {run ? 'Ver prévia de novo' : 'Executar plano'}
            </button>
            {(status.total > 0 || run) && (
              <button type="button" className="mai-btn mai-btn-ghost" onClick={() => onOpen('/central-inteligencia?tab=aprovacoes')}>
                Ver fila de aprovação
              </button>
            )}
          </>
        ) : isBatch ? (
          <>
            <button
              type="button"
              className="mai-btn"
              disabled={batch?.running || rec.targets.length === 0}
              onClick={() => onRun(rec)}
            >
              {batch?.running ? 'Criando…' : rec.actionLabel}
            </button>
            {(status.total > 0 || (batch && !batch.running)) && (
              <button type="button" className="mai-btn mai-btn-ghost" onClick={() => onOpen('/central-inteligencia?tab=aprovacoes')}>
                Ver fila de aprovação
              </button>
            )}
          </>
        ) : rec.path ? (
          <button type="button" className="mai-btn" onClick={() => onOpen(rec.path!)}>{rec.actionLabel}</button>
        ) : null}
        {rec.requiresApproval && <span className="mai-ci-pill">Pede sua aprovação</span>}
      </div>
    </article>
  );
};

export const CentralOverviewView: React.FC<Props> = ({
  overview,
  now,
  nowError,
  analysis,
  analysisLoading,
  onRetryAnalysis,
  batches,
  onRunRecommendation,
  plans,
  onExecutePlan,
  onOpen,
  onShowAgents,
  topicsSlot,
}) => {
  const [openFinding, setOpenFinding] = useState<CentralFinding | null>(null);
  const findingsById = Object.fromEntries(overview.findings.map((f) => [f.id, f]));
  const risks = overview.findings.filter((f) => f.tone !== 'opportunity');
  const opps = overview.findings.filter((f) => f.tone === 'opportunity');
  const comparison = overview.period.comparisonLabel;
  const multiSector = overview.sectors.length > 1;

  const renderFinding = (f: CentralFinding) => (
    <li key={f.id}>
      <button type="button" className={`mai-ci-finding is-${f.tone} is-${f.severity}`} onClick={() => setOpenFinding(f)}>
        <span className="mai-ci-finding-count">{f.count}</span>
        <span className="mai-ci-finding-text">
          <strong>{f.title}</strong>
          <span>{f.what}</span>
        </span>
        <span className="mai-ci-finding-tag">{f.severity === 'high' ? 'Alta' : TONE_LABEL[f.tone]}</span>
      </button>
    </li>
  );

  return (
    <div className="mai-ci">
      <section className="mai-ci-now" aria-label="Agora">
        <div className="mai-ci-section-head">
          <h2>Agora</h2>
          <span className="mai-ci-muted">Mesmos números da tela Hoje, somando as conexões filtradas</span>
        </div>
        {nowError ? (
          <p className="mai-ci-empty">{nowError}</p>
        ) : !now ? (
          <p className="mai-ci-empty" aria-busy="true">Carregando a operação de agora…</p>
        ) : (
          <div className="mai-ci-now-grid">
            <button type="button" onClick={() => onOpen('/chat?filter=ai-attending')}><strong>{now.aiAttending}</strong><span>IA atendendo</span></button>
            <button type="button" onClick={() => onOpen('/chat?filter=awaiting')}><strong>{now.awaitingReply}</strong><span>Aguardando resposta</span></button>
            <button type="button" onClick={() => onOpen('/chat?filter=needs-human')}><strong>{now.needsHuman}</strong><span>Precisam de humano</span></button>
            <button type="button" onClick={() => onOpen('/chat?filter=risk-silent')}><strong>{now.riskSilent}</strong><span>Paradas há 4 a 30 dias</span></button>
            <button type="button" onClick={() => onOpen('/central-inteligencia?tab=aprovacoes')}><strong>{now.approvalsPending}</strong><span>Aprovações pendentes</span></button>
            <button type="button" onClick={() => onOpen('/hoje')}><strong>{now.slaBreached}</strong><span>Fora do prazo</span></button>
          </div>
        )}
      </section>

      <section className="mai-ci-kpis" aria-label={`Indicadores: ${overview.period.label}`}>
        {overview.kpis.map((k) => <KpiCard key={k.key} kpi={k} comparison={comparison} onOpen={onOpen} />)}
      </section>

      {overview.revenue && <CentralRevenuePanel revenue={overview.revenue} multiSector={multiSector} onOpen={onOpen} />}

      <div className="mai-ci-row">
        <section className="mai-ci-panel mai-ci-analysis" aria-label="Análise da IA">
          <div className="mai-ci-section-head">
            <h2>Análise da IA para você</h2>
            {analysis?.available && (
              <span className="mai-ci-muted">Gerada {formatDateTime(analysis.generatedAt)} a partir dos números desta tela</span>
            )}
          </div>
          {analysisLoading && !analysis ? (
            <p className="mai-ci-empty" aria-busy="true">A IA está lendo os números do período…</p>
          ) : analysis && analysis.available ? (
            <>
              <p className="mai-ci-summary">{analysis.summary}</p>
              {analysis.priorities.length > 0 && (
                <ol className="mai-ci-priorities">
                  {analysis.priorities.map((p) => (
                    <li key={p.findingId}>
                      <button
                        type="button"
                        className="mai-ci-link"
                        onClick={() => findingsById[p.findingId] && setOpenFinding(findingsById[p.findingId])}
                      >
                        {p.title}
                      </button>
                      <span>{p.reason}</span>
                    </li>
                  ))}
                </ol>
              )}
              {analysis.limits.length > 0 && (
                <details className="mai-ci-limits">
                  <summary>Limites desta leitura</summary>
                  <ul>{analysis.limits.map((l) => <li key={l}>{l}</li>)}</ul>
                </details>
              )}
            </>
          ) : (
            <div className="mai-ci-empty">
              <p>{analysis?.unavailableReason || 'Leitura da IA indisponível agora.'}</p>
              <button type="button" className="mai-btn mai-btn-ghost" onClick={onRetryAnalysis} disabled={analysisLoading}>
                {analysisLoading ? 'Tentando…' : 'Tentar de novo'}
              </button>
            </div>
          )}
        </section>

        <section className="mai-ci-panel" aria-label="Riscos e oportunidades">
          <div className="mai-ci-section-head">
            <h2>Riscos e oportunidades</h2>
            <span className="mai-ci-muted">Toque para ver os casos</span>
          </div>
          {overview.findings.length === 0 ? (
            <p className="mai-ci-empty">Nenhum risco ou oportunidade com evidência no período.</p>
          ) : (
            <>
              {risks.length > 0 && <ul className="mai-ci-findings">{risks.map(renderFinding)}</ul>}
              {opps.length > 0 && (
                <>
                  <h3 className="mai-ci-subhead">Oportunidades</h3>
                  <ul className="mai-ci-findings">{opps.map(renderFinding)}</ul>
                </>
              )}
            </>
          )}
        </section>
      </div>

      <section className="mai-ci-panel" aria-label="Recomendações">
        <div className="mai-ci-section-head">
          <h2>Recomendações</h2>
          <span className="mai-ci-muted">O que identifiquei, por que importa e o que a ação faz de verdade</span>
        </div>
        {overview.recommendations.length === 0 ? (
          <p className="mai-ci-empty">Nenhuma recomendação com ação concreta no período.</p>
        ) : (
          <div className="mai-ci-recs">
            {overview.recommendations.map((r) => (
              <RecommendationCard
                key={r.id}
                rec={r}
                batch={batches[r.id]}
                plan={plans?.[r.id]}
                onRun={onRunRecommendation}
                onExecutePlan={onExecutePlan}
                onOpen={onOpen}
              />
            ))}
          </div>
        )}
      </section>

      <section className="mai-ci-panel" aria-label="Funcionários digitais">
        <div className="mai-ci-section-head">
          <h2>Funcionários digitais</h2>
          <button type="button" className="mai-ci-link" onClick={onShowAgents}>Ver detalhes</button>
        </div>
        {overview.agents.length === 0 ? (
          <p className="mai-ci-empty">Nenhum funcionário digital cadastrado.</p>
        ) : (
          <div className="mai-ci-table-wrap">
            <table className="mai-ci-table">
              <thead>
                <tr>
                  <th>Funcionário</th>
                  <th>Situação</th>
                  <th>Conversas</th>
                  <th>Reuniões</th>
                  <th>Transferências</th>
                  <th>Sem resposta</th>
                </tr>
              </thead>
              <tbody>
                {overview.agents.map((a) => (
                  <tr key={a.agentId}>
                    <td>
                      <button type="button" className="mai-ci-link" onClick={() => onOpen(a.editPath)}>{a.name}</button>
                      {multiSector && <small className="mai-ci-muted"> · {a.sectorName}</small>}
                    </td>
                    <td><span className={`mai-ci-health is-${a.health}`}>{a.healthLabel}</span></td>
                    <td>{a.conversations}</td>
                    <td>{a.meetingsBooked}</td>
                    <td>{a.transfers}</td>
                    <td className={a.emptyTurns > 0 ? 'is-bad' : ''}>{a.emptyTurns}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {multiSector && (
        <section className="mai-ci-panel" aria-label="Desempenho por conexão">
          <div className="mai-ci-section-head">
            <h2>Desempenho por conexão</h2>
          </div>
          <div className="mai-ci-table-wrap">
            <table className="mai-ci-table">
              <thead>
                <tr>
                  <th>Conexão</th>
                  <th>Conversas da IA</th>
                  <th>Clientes que escreveram</th>
                  <th>Novos contatos</th>
                  <th>Reuniões da IA</th>
                  <th>Transferências</th>
                  <th>1ª resposta</th>
                  <th>Funcionários ativos</th>
                </tr>
              </thead>
              <tbody>
                {overview.sectorPerformance.map((s) => (
                  <tr key={s.sectorId}>
                    <td>{s.sectorName}</td>
                    <td>{s.conversationsAi}<small className="mai-ci-muted"> (antes {s.previousConversationsAi})</small></td>
                    <td>{s.conversationsActive}</td>
                    <td>{s.newContacts}</td>
                    <td>{s.meetingsBookedByAi}</td>
                    <td>{s.transfers}</td>
                    <td>{s.medianFirstResponseMinutes === null ? '—' : formatMinutes(s.medianFirstResponseMinutes)}</td>
                    <td>{s.agentsActive} de {s.agentsTotal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="mai-ci-row">
        <section className="mai-ci-panel" aria-label="Evolução no período">
          <div className="mai-ci-section-head">
            <h2>Mensagens por dia</h2>
            <span className="mai-ci-muted">{overview.period.label}</span>
          </div>
          <CentralTrendChart points={overview.daily} />
        </section>

        <section className="mai-ci-panel" aria-label="Origem e canais">
          <div className="mai-ci-section-head">
            <h2>De onde vieram os novos contatos</h2>
          </div>
          {overview.origins.length === 0 ? (
            <p className="mai-ci-empty">Nenhum contato novo no período.</p>
          ) : (
            <ul className="mai-ci-bars">
              {(() => {
                const max = Math.max(...overview.origins.map((o) => o.count));
                return overview.origins.map((o) => (
                  <li key={o.key} title={o.hint || undefined}>
                    <span className="mai-ci-bar-label">{o.label}</span>
                    <span className="mai-ci-bar-track"><span style={{ width: `${(o.count / max) * 100}%` }} /></span>
                    <span className="mai-ci-bar-value">{o.count}</span>
                  </li>
                ));
              })()}
            </ul>
          )}
          <h3 className="mai-ci-subhead">Canais</h3>
          <ul className="mai-ci-channels">
            {overview.channels.map((c) => (
              <li key={c.key} className={`is-${c.status}`}>
                <span>{c.label}</span>
                {c.status === 'connected' ? (
                  <span>
                    Conectado{c.conversations != null ? ` · ${c.conversations} clientes escreveram` : ''}
                    {c.detail ? ` · ${c.detail}` : ''}
                  </span>
                ) : (
                  <span>Não conectado{c.detail ? ` · ${c.detail}` : ''}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      </div>

      {topicsSlot}

      {overview.limits.length > 0 && (
        <section className="mai-ci-panel mai-ci-footnote" aria-label="Limites">
          <h2>O que estes números não dizem</h2>
          <ul>{overview.limits.map((l) => <li key={l}>{l}</li>)}</ul>
          <p className="mai-ci-muted">
            Atualizado {formatDateTime(overview.generatedAt)} · período de {formatDateTime(overview.period.from)} até {formatDateTime(overview.period.to)} ({overview.period.timezone})
          </p>
        </section>
      )}

      <FindingDrawer finding={openFinding} onClose={() => setOpenFinding(null)} onOpen={(p) => { setOpenFinding(null); onOpen(p); }} />
    </div>
  );
};
